/**
 * Achmea-tarievenkaart — bekijk en pas de officiële Achmea/Interpolis-tarieven aan.
 * Overrides worden als setting `achmea_tarieven` opgeslagen; de vaste kaart in de
 * code blijft de standaard (reset per code mogelijk). Gebruikt door de Achmea
 * factuur-export én de offerte-check.
 */
import React, { useEffect, useState } from 'react';
import { Box, Typography, TextField, Button, CircularProgress, Stack, Chip, Alert } from '@mui/material';
import { SectionHeader } from '../adminSettingsCommon';
import { BACKEND_URL } from '../../../utils/apiConfig';
import { authFetch } from '../../../utils/authHelpers';

export default function AchmeaTarieven() {
  const [rows, setRows] = useState(null);
  const [edits, setEdits] = useState({});
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const [error, setError] = useState(null);

  const load = async () => {
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/achmea-export/tarieven`);
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Tarieven laden mislukt');
      setRows(d.tarieven || []);
      setEdits({});
    } catch (e) { setError(e.message); setRows([]); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true); setError(null); setMsg(null);
    try {
      const tarieven = {};
      for (const row of rows) {
        tarieven[row.code] = edits[row.code] !== undefined ? Number(edits[row.code]) : row.tarief;
      }
      const r = await authFetch(`${BACKEND_URL}/Api/achmea-export/tarieven`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tarieven }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Opslaan mislukt');
      setMsg('Tarieven opgeslagen ✔ — direct actief in de factuur-export en offerte-check.');
      load();
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  return (
    <Box>
      <SectionHeader
        title="Achmea-tarievenkaart"
        subtitle="Officiële tarieven (ex btw) per code — gebruikt door de Achmea factuur-export en de offerte-check. Aangepaste tarieven krijgen een badge; de standaard blijft zichtbaar."
      />
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {msg && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMsg(null)}>{msg}</Alert>}
      {rows === null ? <CircularProgress size={20} /> : (
        <>
          <Stack spacing={0.5}>
            <Stack direction="row" spacing={1} sx={{ px: 0.5, pb: 0.5 }}>
              <Typography variant="caption" sx={{ width: 50, fontWeight: 800 }}>Code</Typography>
              <Typography variant="caption" sx={{ flex: 1, fontWeight: 800 }}>Functie</Typography>
              <Typography variant="caption" sx={{ width: 120, fontWeight: 800, textAlign: 'right' }}>Tarief (€)</Typography>
              <Box sx={{ width: 100 }} />
            </Stack>
            {rows.map((row) => {
              const val = edits[row.code] !== undefined ? edits[row.code] : row.tarief;
              const overridden = Math.abs(Number(val) - row.standaard) > 0.001;
              return (
                <Stack key={row.code} direction="row" spacing={1} alignItems="center" sx={{ px: 0.5 }}>
                  <Typography variant="caption" sx={{ width: 50, fontWeight: 700 }}>{row.code}</Typography>
                  <Typography variant="caption" sx={{ flex: 1 }} noWrap>
                    {row.functie}{row.achmea && row.achmea !== row.functie ? ` — ${row.achmea}` : ''}
                  </Typography>
                  <TextField size="small" type="number" value={val}
                    inputProps={{ min: 0, step: 0.01, style: { textAlign: 'right' } }}
                    onChange={(e) => setEdits((m) => ({ ...m, [row.code]: e.target.value }))}
                    sx={{ width: 120 }} />
                  <Box sx={{ width: 100 }}>
                    {overridden ? (
                      <Chip size="small" label={`std ${row.standaard}`} onDelete={() => setEdits((m) => ({ ...m, [row.code]: row.standaard }))}
                        sx={{ height: 20, fontSize: 10, bgcolor: '#fef3c7', color: '#92400e' }} />
                    ) : null}
                  </Box>
                </Stack>
              );
            })}
          </Stack>
          <Button variant="contained" onClick={save} disabled={saving}
            startIcon={saving ? <CircularProgress size={14} color="inherit" /> : null}
            sx={{ mt: 2, textTransform: 'none', fontWeight: 800 }}>
            Opslaan
          </Button>
        </>
      )}
    </Box>
  );
}
